/* eslint-disable no-nested-ternary */
/* eslint-disable  */
import React, { useEffect } from 'react';
import { Box, TextField, Button, Grid, Card, CardContent, CardHeader, FormControlLabel, Checkbox } from '@mui/material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import AnimateButton from 'ui-component/extended/AnimateButton';
import { globalColor } from 'views/ConstantColors';
import { useDepartment } from 'hooks/api/terminology/department/useDepartment';
import Swal from 'sweetalert2';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const DepartmentSchema = Yup.object({
    projectName: Yup.string().required('Department name is required'),
    groupCode: Yup.string().required('Branch code is required'),
    groupCodeDescription: Yup.string().required('Description is required'),
    isActive: Yup.boolean()
});

export const DepartmentForm = ({ initialData, onClear }) => {
    const isEditMode = Boolean(initialData?.id);
    const { createDepartment, updateDepartment } = useDepartment();

    useEffect(() => {
        console.log('Form initialData changed:', initialData);
    }, [initialData]);

    const initialValues = {
        id: initialData?.id || 0,
        projectName: initialData?.projectName || '',
        groupCode: initialData?.groupCode || '',
        groupCodeDescription: initialData?.groupCodeDescription || '',
        isActive: initialData?.isActive ?? true,
        isDeleted: initialData?.isDeleted ?? false,
        createdBy: initialData?.createdBy || '',
        lastModifiedBy: initialData?.lastModifiedBy || '',
        createdAt: initialData?.createdAt || new Date().toISOString(),
        lastModifiedAt: initialData?.lastModifiedAt || new Date().toISOString()
    };

    const handleFormSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const formattedData = {
                id: values.id,
                projectName: values.projectName,
                groupCode: values.groupCode,
                groupCodeDescription: values.groupCodeDescription,
                isActive: values.isActive,
                isDeleted: values.isDeleted,
                createdBy: values.createdBy,
                lastModifiedBy: values.lastModifiedBy,
                createdAt: values.createdAt,
                lastModifiedAt: new Date().toISOString()
            };


            if (isEditMode && values.id && values.id !== 0) {
                await updateDepartment(values.id, formattedData);
                Swal.fire('Success', 'Department updated successfully!', 'success');
            } else {
                await createDepartment(formattedData);
                Swal.fire('Success', 'Department added successfully!', 'success');
                resetForm();
            }

            if (onClear) {
                onClear();
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            Swal.fire('Error', error.message || 'There was an error processing your request.', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleReset = (resetForm) => {
        resetForm();
        if (onClear) {
            onClear();
        }
    };

    return (
        <Card elevation={3} sx={{ mb: 4 }}>
            <CardHeader
                title={isEditMode ? 'Edit Department' : 'Add New Department'}
                sx={{
                    backgroundColor: globalColor,
                    color: 'white',
                    '.MuiCardHeader-title': { color: 'white', fontSize: '1.2rem', fontWeight: 'bold' }
                }}
            />
            <CardContent>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Formik
                        initialValues={initialValues}
                        validationSchema={DepartmentSchema}
                        onSubmit={handleFormSubmit}
                        enableReinitialize
                        key={initialData?.id || 'new'}
                    >
                        {({ errors, touched, values, setFieldValue, resetForm, isSubmitting }) => (
                            <Form>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Field
                                            as={TextField}
                                            fullWidth
                                            name="projectName"
                                            label="Department Name"
                                            error={touched.projectName && Boolean(errors.projectName)}
                                            helperText={touched.projectName && errors.projectName}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Field
                                            as={TextField}
                                            fullWidth
                                            name="groupCode"
                                            label="Department Code"
                                            error={touched.groupCode && Boolean(errors.groupCode)}
                                            helperText={touched.groupCode && errors.groupCode}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Field
                                            as={TextField}
                                            fullWidth
                                            name="groupCodeDescription"
                                            label="Description"
                                            multiline
                                            rows={3}
                                            error={touched.groupCodeDescription && Boolean(errors.groupCodeDescription)}
                                            helperText={touched.groupCodeDescription && errors.groupCodeDescription}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={values.isActive}
                                                    onChange={(e) => setFieldValue('isActive', e.target.checked)}
                                                    name="isActive"
                                                />
                                            }
                                            label="Active"
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                            <AnimateButton>
                                                <Button
                                                    type="button"
                                                    variant="outlined"
                                                    onClick={() => handleReset(resetForm)}
                                                    disabled={isSubmitting}
                                                    sx={{
                                                        borderColor: globalColor,
                                                        color: globalColor,
                                                        '&:hover': {
                                                            borderColor: 'rgb(0, 100, 0)',
                                                            color: 'rgb(0, 100, 0)'
                                                        }
                                                    }}
                                                >
                                                    {isEditMode ? 'Cancel' : 'Reset'}
                                                </Button>
                                            </AnimateButton>
                                            <AnimateButton>
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    disabled={isSubmitting}
                                                    sx={{
                                                        bgcolor: globalColor,
                                                        '&:hover': {
                                                            bgcolor: 'rgb(0, 100, 0)'
                                                        }
                                                    }}
                                                >
                                                    {isSubmitting
                                                        ? isEditMode
                                                            ? 'Updating...'
                                                            : 'Adding...'
                                                        : isEditMode
                                                        ? 'Update Department'
                                                        : 'Add Department'}
                                                </Button>
                                            </AnimateButton>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Form>
                        )}
                    </Formik>
                </LocalizationProvider>
            </CardContent>
        </Card>
    );
};
